import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

import { ZoomStatus } from 'types';

interface IStatusMeta {
  color: string;
  i18nKey: string;
  spinner: boolean;
}

/** Status colours follow ТЗ §10 (tailwind palette mapped to hex). */
export const ZOOM_STATUS_META: Record<ZoomStatus, IStatusMeta> = {
  [ZoomStatus.MATCHED]: {
    color: '#64748B',
    i18nKey: 'pages.zoom.status.matched',
    spinner: false,
  },
  [ZoomStatus.DOWNLOADING]: {
    color: '#0EA5E9',
    i18nKey: 'pages.zoom.status.downloading',
    spinner: true,
  },
  [ZoomStatus.UPLOADED]: {
    color: '#14B8A6',
    i18nKey: 'pages.zoom.status.uploaded',
    spinner: false,
  },
  [ZoomStatus.AI_PROCESSING]: {
    color: '#A855F7',
    i18nKey: 'pages.zoom.status.aiProcessing',
    spinner: true,
  },
  [ZoomStatus.AI_COMPLETED]: {
    color: '#6366F1',
    i18nKey: 'pages.zoom.status.aiCompleted',
    spinner: false,
  },
  [ZoomStatus.PUBLISHED]: {
    color: '#10B981',
    i18nKey: 'pages.zoom.status.published',
    spinner: false,
  },
  [ZoomStatus.FAILED]: {
    color: '#EF4444',
    i18nKey: 'pages.zoom.status.failed',
    spinner: false,
  },
  [ZoomStatus.SKIPPED]: {
    color: '#A1A1AA',
    i18nKey: 'pages.zoom.status.skipped',
    spinner: false,
  },
  [ZoomStatus.NO_RECORDING]: {
    color: '#9CA3AF',
    i18nKey: 'pages.zoom.status.noRecording',
    spinner: false,
  },
};

interface IZoomStatusBadgeProps {
  status: ZoomStatus;
  withText?: boolean;
  /**
   * Whether in-progress statuses (downloading / AI) show a spinning wheel.
   * `true` for a real record actively in that state; pass `false` when the
   * badge is just a static legend label (e.g. dashboard pipeline counters).
   */
  animated?: boolean;
}

/** Coloured pill for a LessonRecording pipeline status. */
export const ZoomStatusBadge = ({
  status,
  withText = true,
  animated = true,
}: IZoomStatusBadgeProps) => {
  const { t } = useTranslation();
  const meta = ZOOM_STATUS_META[status];

  return (
    <Badge color={meta.color}>
      {meta.spinner && animated ? <Spinner /> : <Dot />}
      {withText ? <span>{t(meta.i18nKey)}</span> : null}
    </Badge>
  );
};

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Badge = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  line-height: 20px;
  white-space: nowrap;
  color: ${({ color }) => color};
  background: ${({ color }) => `${color}1a`};
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
`;

const Spinner = styled.span`
  width: 10px;
  height: 10px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;
